(() => {
  if (customElements.get('rashi-collection-carousel')) return;

  class RashiCollectionCarousel extends HTMLElement {
    connectedCallback() {
      this.viewport = this.querySelector('[data-rashi-viewport]');
      this.track = this.querySelector('[data-rashi-track]');
      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.autoplay = this.dataset.autoplay === 'true';
      this.desktopVisible = 9;
      this.tabletVisible = Math.max(1, Number(this.dataset.tabletVisible) || 5);
      this.delay = Math.max(2000, Number(this.dataset.delay) || 3000);
      this.resumeDelay = Math.max(5000, Number(this.dataset.resumeDelay) || 10000);
      this.transition = Math.max(200, Number(this.dataset.transition) || 500);
      this.timer = null;
      this.resumeTimer = null;
      this.scrollTimer = null;
      this.animationFrame = null;
      this.resizeTimer = null;
      this.animating = false;
      this.reordering = false;
      this.interacting = false;

      if (!this.viewport || !this.track) return;
      if (this.slides().length < 2) {
        this.updateCardWidth();
        return;
      }

      this.onPointerDown = () => {
        this.interacting = true;
        this.pauseForInteraction();
      };
      this.onPointerUp = () => {
        if (!this.interacting) return;
        this.interacting = false;
        this.pauseForInteraction();
      };
      this.onClick = () => this.pauseForInteraction();
      this.onWheel = () => this.pauseForInteraction();
      this.onFocusIn = () => this.pauseForInteraction();
      this.onPrevious = () => this.navigate(-1);
      this.onNext = () => this.navigate(1);
      this.onScroll = () => this.handleScroll();
      this.onVisibility = () => {
        if (document.hidden) this.stop();
        else this.schedule();
      };
      this.onResize = () => {
        clearTimeout(this.resizeTimer);
        this.resizeTimer = window.setTimeout(() => this.configure(), 140);
      };

      this.viewport.addEventListener('pointerdown', this.onPointerDown, { passive: true });
      this.viewport.addEventListener('touchstart', this.onPointerDown, { passive: true });
      window.addEventListener('pointerup', this.onPointerUp, { passive: true });
      window.addEventListener('pointercancel', this.onPointerUp, { passive: true });
      this.previousButton = this.querySelector('[data-rashi-previous]');
      this.nextButton = this.querySelector('[data-rashi-next]');
      this.previousButton?.addEventListener('click', this.onPrevious);
      this.nextButton?.addEventListener('click', this.onNext);
      this.viewport.addEventListener('click', this.onClick);
      this.viewport.addEventListener('wheel', this.onWheel, { passive: true });
      this.viewport.addEventListener('focusin', this.onFocusIn);
      this.viewport.addEventListener('scroll', this.onScroll, { passive: true });
      document.addEventListener('visibilitychange', this.onVisibility);

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.onResize);
        this.resizeObserver.observe(this.viewport);
      } else {
        window.addEventListener('resize', this.onResize, { passive: true });
      }

      this.configure();
    }

    disconnectedCallback() {
      this.stop();
      clearTimeout(this.resumeTimer);
      clearTimeout(this.scrollTimer);
      clearTimeout(this.resizeTimer);
      if (!this.viewport) return;
      this.viewport.removeEventListener('pointerdown', this.onPointerDown);
      this.viewport.removeEventListener('touchstart', this.onPointerDown);
      window.removeEventListener('pointerup', this.onPointerUp);
      window.removeEventListener('pointercancel', this.onPointerUp);
      this.previousButton?.removeEventListener('click', this.onPrevious);
      this.nextButton?.removeEventListener('click', this.onNext);
      this.viewport.removeEventListener('click', this.onClick);
      this.viewport.removeEventListener('wheel', this.onWheel);
      this.viewport.removeEventListener('focusin', this.onFocusIn);
      this.viewport.removeEventListener('scroll', this.onScroll);
      document.removeEventListener('visibilitychange', this.onVisibility);
      if (this.resizeObserver) this.resizeObserver.disconnect();
      else window.removeEventListener('resize', this.onResize);
    }

    slides() {
      return Array.from(this.track?.querySelectorAll('[data-rashi-slide]') || []);
    }

    configure() {
      this.stop();
      clearTimeout(this.resumeTimer);
      this.restoreOriginalOrder();
      this.updateCardWidth();
      const canSlide = this.viewport.scrollWidth > this.viewport.clientWidth + 1;
      this.querySelectorAll('[data-rashi-previous], [data-rashi-next]').forEach((button) => {
        button.disabled = !canSlide;
      });
      if (this.mobile && this.slides().length > 1) this.track.prepend(this.track.lastElementChild);
      this.reordering = true;
      this.viewport.scrollLeft = this.positionFor(this.slides()[this.mobile ? 1 : 0]);
      this.reordering = false;
      this.updateDots();
      this.schedule();
    }

    schedule(wait = this.delay) {
      this.stop();
      if (!this.autoplay || this.reduceMotion.matches || document.hidden || this.slides().length < 2 || this.viewport.scrollWidth <= this.viewport.clientWidth + 1) return;
      this.timer = window.setTimeout(() => this.advance(), wait);
    }

    stop() {
      clearTimeout(this.timer);
      this.timer = null;
      if (this.animationFrame) cancelAnimationFrame(this.animationFrame);
      this.animationFrame = null;
      this.animating = false;
      if (this.viewport) this.viewport.style.scrollSnapType = '';
    }

    pauseForInteraction() {
      this.stop();
      clearTimeout(this.scrollTimer);
      this.scrollTimer = window.setTimeout(() => this.normalizeToNearest(), 120);
      clearTimeout(this.resumeTimer);
      this.resumeTimer = window.setTimeout(() => {
        if (this.interacting) return;
        this.normalizeToNearest();
        this.schedule();
      }, this.resumeDelay);
    }

    navigate(direction) {
      this.stop();
      clearTimeout(this.resumeTimer);
      clearTimeout(this.scrollTimer);
      this.normalizeToNearest();
      this.advance(direction);
    }

    advance(direction = 1) {
      if (this.slides().length < 2 || this.animating || this.viewport.scrollWidth <= this.viewport.clientWidth + 1) return;
      const baseline = this.mobile ? 1 : 0;
      this.viewport.style.scrollSnapType = 'none';
      if (direction < 0) {
        this.reordering = true;
        this.track.prepend(this.track.lastElementChild);
        this.viewport.scrollLeft = this.positionFor(this.slides()[baseline + 1]);
        this.reordering = false;
      }
      const target = this.slides()[baseline + (direction < 0 ? 0 : 1)];
      const distance = this.positionFor(target);

      this.animating = true;
      const startLeft = this.viewport.scrollLeft;
      const startTime = performance.now();
      const duration = this.reduceMotion.matches ? 0 : this.transition;
      const animate = (time) => {
        if (!this.animating || !this.isConnected) return;
        const progress = duration ? Math.min(1, (time - startTime) / duration) : 1;
        const eased = 1 - Math.pow(1 - progress, 3);
        this.viewport.scrollLeft = startLeft + ((distance - startLeft) * eased);
        if (progress < 1) {
          this.animationFrame = requestAnimationFrame(animate);
          return;
        }
        this.animationFrame = null;
        this.reordering = true;
        if (direction > 0) this.track.append(this.track.firstElementChild);
        this.viewport.scrollLeft = this.positionFor(this.slides()[baseline]);
        this.viewport.style.scrollSnapType = '';
        this.reordering = false;
        this.animating = false;
        this.updateDots();
        // Keep slide starts one configured interval apart, including transition time.
        this.schedule(Math.max(0, this.delay - (performance.now() - startTime)));
      };
      this.animationFrame = requestAnimationFrame(animate);
    }

    handleScroll() {
      if (this.animating || this.reordering) return;
      clearTimeout(this.scrollTimer);
      this.scrollTimer = window.setTimeout(() => this.normalizeToNearest(), 180);
    }

    normalizeToNearest() {
      if (this.animating || this.reordering) return;
      const slides = this.slides();
      if (slides.length < 2) return;

      const current = this.viewport.scrollLeft;
      let nearestIndex = 0;
      let nearestDistance = Infinity;
      slides.forEach((slide, index) => {
        const distance = Math.abs(this.positionFor(slide) - current);
        if (distance < nearestDistance) {
          nearestDistance = distance;
          nearestIndex = index;
        }
      });

      const baseline = this.mobile ? 1 : 0;
      if (nearestIndex !== baseline) {
        this.reordering = true;
        for (let index = baseline; index < nearestIndex; index += 1) this.track.append(this.track.firstElementChild);
        for (let index = nearestIndex; index < baseline; index += 1) this.track.prepend(this.track.lastElementChild);
        this.viewport.scrollLeft = this.positionFor(this.slides()[baseline]);
        this.reordering = false;
      }
      this.updateDots();
    }

    restoreOriginalOrder() {
      const slides = this.slides().sort((a, b) => Number(a.dataset.rashiIndex) - Number(b.dataset.rashiIndex));
      this.reordering = true;
      slides.forEach((slide) => this.track.append(slide));
      this.viewport.scrollLeft = 0;
      this.reordering = false;
      this.updateDots();
    }

    updateCardWidth() {
      this.mobile = window.matchMedia('(max-width: 749px)').matches;
      if (this.mobile) {
        const width = Math.min(280, this.viewport.clientWidth * .68);
        this.style.setProperty('--sbr-computed-card-width', `${width}px`);
        this.style.setProperty('--sbr-center-inset', `${(this.viewport.clientWidth - width) / 2}px`);
        return;
      }
      this.style.setProperty('--sbr-center-inset', '0px');
      const cardsVisible = window.matchMedia('(min-width: 1200px)').matches ? this.desktopVisible : this.tabletVisible;
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      const width = Math.max(1, (this.viewport.clientWidth - (gap * (cardsVisible - 1))) / cardsVisible);
      this.style.setProperty('--sbr-computed-card-width', `${width}px`);
    }

    positionFor(slide) {
      if (!slide) return 0;
      const inset = this.mobile ? (this.viewport.clientWidth - slide.getBoundingClientRect().width) / 2 : 0;
      return slide.getBoundingClientRect().left - this.viewport.getBoundingClientRect().left + this.viewport.scrollLeft - inset;
    }

    updateDots() {
      const first = this.slides()[this.mobile ? 1 : 0];
      const activeIndex = first ? first.dataset.rashiIndex : '0';
      this.querySelectorAll('[data-rashi-dot]').forEach((dot) => {
        dot.classList.toggle('is-active', dot.dataset.rashiDot === activeIndex);
      });
    }
  }

  customElements.define('rashi-collection-carousel', RashiCollectionCarousel);
})();
